import React, { useState, useEffect } from 'react';
import { createApiClient } from '../utils/api';
import './ConnectionStatus.css';

interface ConnectionStatusProps {
  apiUrl: string;
}

const ConnectionStatus: React.FC<ConnectionStatusProps> = ({ apiUrl }) => {
  const [status, setStatus] = useState<'checking' | 'connected' | 'error'>('checking');
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const checkConnection = async () => {
    try {
      setStatus('checking');
      const client = createApiClient(5000); // 5 second timeout for health check
      const response = await client.get('/health');
      
      if (response.status === 200) {
        setStatus('connected');
        setErrorMessage('');
      } else {
        setStatus('error');
        setErrorMessage(`Server returned status ${response.status}`);
      }
    } catch (err: any) {
      setStatus('error');
      if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
        setErrorMessage('Connection timeout');
      } else if (err.code === 'ERR_NETWORK' || err.message.includes('Network Error')) {
        setErrorMessage('Network error - server unreachable');
      } else {
        setErrorMessage(err.message || 'Unknown error');
      }
    } finally {
      setLastChecked(new Date());
    }
  };

  useEffect(() => {
    checkConnection();
    // Check connection every 30 seconds
    const interval = setInterval(checkConnection, 30000);
    return () => clearInterval(interval);
  }, [apiUrl]);

  const getStatusIcon = () => {
    switch (status) {
      case 'checking':
        return '🔄';
      case 'connected':
        return '✅';
      case 'error':
        return '❌';
      default:
        return '❓';
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'checking':
        return 'Checking connection...';
      case 'connected':
        return 'Connected to server';
      case 'error':
        return `Connection failed: ${errorMessage}`;
      default:
        return 'Unknown status';
    }
  };

  return (
    <div className="connection-status">
      <div className="status-indicator">
        <span className="status-icon">{getStatusIcon()}</span>
        <span className="status-text">{getStatusText()}</span>
      </div>
      {lastChecked && (
        <div className="last-checked">
          Last checked: {lastChecked.toLocaleTimeString()}
        </div>
      )}
      <button 
        className="retry-button" 
        onClick={checkConnection}
        disabled={status === 'checking'}
      >
        Retry Connection
      </button>
    </div>
  );
};

export default ConnectionStatus;
